/**
 * Zeitreihen und Lücken.
 *
 * Eine fehlende Viertelstunde ist eine **Lücke** und wird als solche geführt.
 * Sie wird nicht stillschweigend mit dem Nachbarwert gefüllt: der Unterschied
 * zwischen „das Haus hat nichts verbraucht" und „wir wissen es nicht" ist der
 * Unterschied zwischen einer Aussage und einer Erfindung.
 */
import type { ValueQuality } from "@hausmeter/domain";
import { bucketsBetween, type Resolution, type TimeRange } from "./grid.js";

/** Ein Wert an einer Bucket-Kante. `bucketStart` liegt immer auf dem Raster. */
export interface BucketValue {
  readonly bucketStart: Date;
  readonly value: number;
  readonly quality: ValueQuality;
  /**
   * Bei einem **verdichteten** Wert: welcher Anteil der zugrunde liegenden
   * Buckets vorlag, `0 < coverage < 1`. Fehlt die Angabe, war der Abschnitt
   * vollständig. Ein Tag mit 94 von 96 Viertelstunden ist eine Teilsumme mit
   * Abdeckung, keine Lücke — und keine volle Summe (HM-105).
   */
  readonly coverage?: number;
}

/** Leistungswerte werden verdichtet gespeichert: Mittel, Minimum, Maximum, Anzahl. */
export interface PowerBucketValue {
  readonly bucketStart: Date;
  readonly mean: number;
  readonly min: number;
  readonly max: number;
  readonly sampleCount: number;
  readonly quality: ValueQuality;
}

/** Eine Reihe mit ihrem Raster und ihrem Fenster. */
export interface Series {
  readonly resolution: Resolution;
  readonly range: TimeRange;
  readonly values: readonly BucketValue[];
}

/** Ein zusammenhängender Bereich ohne Werte. */
export interface Gap {
  readonly from: Date;
  readonly to: Date;
  readonly missingBuckets: number;
}

/**
 * Findet die Lücken einer Reihe. Erwartet aufsteigend sortierte Werte auf dem Raster;
 * Werte außerhalb des Fensters werden ignoriert.
 */
export function findGaps(series: Series): readonly Gap[] {
  const expected = bucketsBetween(series.range.from, series.range.to, series.resolution);
  const present = new Set(series.values.map((value) => value.bucketStart.getTime()));

  const gaps: Gap[] = [];
  let runStart: Date | null = null;
  let runCount = 0;
  const stepMs = series.resolution * 60_000;

  for (const bucket of expected) {
    if (present.has(bucket.getTime())) {
      if (runStart !== null) {
        gaps.push({
          from: runStart,
          to: bucket,
          missingBuckets: runCount,
        });
        runStart = null;
        runCount = 0;
      }
      continue;
    }
    if (runStart === null) runStart = bucket;
    runCount += 1;
  }

  if (runStart !== null) {
    gaps.push({
      from: runStart,
      to: new Date(runStart.getTime() + runCount * stepMs),
      missingBuckets: runCount,
    });
  }
  return gaps;
}

/**
 * Ab welchem Abstand zwei Werte eine Lücke zwischen sich haben (#99) — die eine
 * Regel für die Lücken der API und die Linie des Diagramms.
 *
 * **Eine Lücke ist eine ausgebliebene Auslesung, kein leerer Abschnitt.** Ohne
 * Takt gilt das 1,5-fache des Rasters, wie immer. Mit Takt das 1,5-fache des
 * Takts **plus ein Rasterschritt** — eine Auslesung kann in den nächsten Abschnitt
 * fallen. Eine ausgefallene Auslesung (zwei Takte Abstand) liegt darüber und
 * bleibt eine Lücke (Regel 8).
 *
 * **Der gemeldete Takt wird gegen die Daten gehalten** (`observedMs`, der häufigste
 * Abstand): Gilt er erst seit gestern, oder liefert das Gerät öfter, als es
 * meldet, zählt der kleinere. Sonst überbrückte ein Takt von zwölf Stunden einen
 * zehnstündigen Ausfall in Werten, die im Minutentakt kamen.
 */
export function readingGapThresholdMs(stepMs: number, cadenceMs: number | null, observedMs: number | null): number {
  const cadence = cadenceMs === null ? null : Math.min(cadenceMs, observedMs ?? cadenceMs);
  if (cadence === null || cadence <= stepMs) return stepMs * GAP_FACTOR;
  return cadence * GAP_FACTOR + stepMs;
}

/** Spielraum gegen ein Raster, das über eine Zeitumstellung eine Stunde kürzer oder länger ist. */
const GAP_FACTOR = 1.5;

/**
 * Der häufigste Abstand zweier Zeitpunkte, bei Gleichstand der kleinere — kommt
 * jeder nur einmal vor, der untere Median. `null` unter zwei Zeitpunkten.
 */
export function typicalSpacingMs(times: readonly number[]): number | null {
  const deltas: number[] = [];
  for (let index = 1; index < times.length; index += 1) {
    const delta = times[index]! - times[index - 1]!;
    if (delta > 0) deltas.push(delta);
  }
  if (deltas.length === 0) return null;
  const counts = new Map<number, number>();
  for (const delta of deltas) counts.set(delta, (counts.get(delta) ?? 0) + 1);
  let common: number | null = null;
  let seen = 1;
  for (const [delta, count] of counts) {
    if (count > seen || (count === seen && common !== null && delta < common)) {
      common = delta;
      seen = count;
    }
  }
  if (common !== null) return common;
  deltas.sort((a, b) => a - b);
  return deltas[Math.floor((deltas.length - 1) / 2)]!;
}

/**
 * Die Lücken einer **Messreihe mit Auslesetakt** (#99): Ein Lauf leerer Abschnitte
 * ist nur dann eine Lücke, wenn die Werte davor und danach — oder Fensterrand und
 * Wert — weiter auseinander liegen als `readingGapThresholdMs`. Ohne Takt dieselben
 * Lücken wie `findGaps`.
 */
export function findReadingGaps(series: Series, cadenceMs: number | null): readonly Gap[] {
  const gaps = findGaps(series);
  if (cadenceMs === null) return gaps;
  const stepMs = series.resolution * 60_000;
  const observed = typicalSpacingMs(series.values.map((value) => value.bucketStart.getTime()));
  const threshold = readingGapThresholdMs(stepMs, cadenceMs, observed);
  return gaps.filter((gap) => (gap.missingBuckets + 1) * stepMs > threshold);
}

/** Der Deckungsgrad zu diesen Lücken: der Anteil der Abschnitte, die in keiner liegen. */
export function coverageOutside(series: Series, gaps: readonly Gap[]): number {
  const expected = bucketsBetween(series.range.from, series.range.to, series.resolution).length;
  if (expected === 0) return 1;
  const missing = gaps.reduce((sum, gap) => sum + gap.missingBuckets, 0);
  return Math.max(0, Math.min(1, (expected - missing) / expected));
}

/**
 * Füllt Lücken **sichtbar** auf: die eingesetzten Werte tragen die Qualität
 * `interpolated` und sind damit in der API und im Diagramm unterscheidbar.
 * Nur für die Anzeige — niemals für die Speicherung.
 */
export function interpolateForDisplay(series: Series): Series {
  const byBucket = new Map(series.values.map((value) => [value.bucketStart.getTime(), value]));
  const expected = bucketsBetween(series.range.from, series.range.to, series.resolution);
  const filled: BucketValue[] = [];

  for (const bucket of expected) {
    const existing = byBucket.get(bucket.getTime());
    if (existing) {
      filled.push(existing);
      continue;
    }

    const before = lastBefore(series.values, bucket);
    const after = firstAfter(series.values, bucket);
    if (!before || !after) continue; // Ränder werden nicht extrapoliert.

    const span = after.bucketStart.getTime() - before.bucketStart.getTime();
    const position = (bucket.getTime() - before.bucketStart.getTime()) / span;
    filled.push({
      bucketStart: bucket,
      value: before.value + (after.value - before.value) * position,
      quality: "interpolated",
    });
  }
  return { ...series, values: filled };
}

/** Deckungsgrad einer Reihe: 1 bedeutet lückenlos. */
export function coverage(series: Series): number {
  const expected = bucketsBetween(series.range.from, series.range.to, series.resolution).length;
  if (expected === 0) return 1;
  // Je Bucket einmal, und ein verdichteter Bucket nur mit seinem Anteil (HM-105).
  const byBucket = new Map<number, number>();
  for (const value of series.values) byBucket.set(value.bucketStart.getTime(), value.coverage ?? 1);
  let present = 0;
  for (const share of byBucket.values()) present += share;
  return Math.min(1, present / expected);
}

function lastBefore(values: readonly BucketValue[], bucket: Date): BucketValue | undefined {
  let found: BucketValue | undefined;
  for (const value of values) {
    if (value.bucketStart < bucket) found = value;
    else break;
  }
  return found;
}

function firstAfter(values: readonly BucketValue[], bucket: Date): BucketValue | undefined {
  return values.find((value) => value.bucketStart > bucket);
}
